import moment from "moment";
import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { ioaService } from "@/services";
import * as utils from "@/services/UtilitiesService";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useCaseFileStore } from "@/stores/case-file-store";

import type { Moment } from "moment";
import { isApiError } from "@/services/Api";
import type InstanceOfAdvice from "@/models/InstanceOfAdvice";

interface State {
  instanceOfAdvices: readonly InstanceOfAdvice[];
  moduleStatus: ModuleStatus;
}
export const useIoaStore = defineStore("ioa", {
  scope: "case",
  state: (): State => ({
    instanceOfAdvices: [],
    moduleStatus: ModuleStatus.Initial,
  }),
  getters: {
    mappedInstances(): { [x: string]: InstanceOfAdvice } {
      return this.instanceOfAdvices
        .flatMap((ioa) => ioa.fileReferences.map((fr) => ({ [fr]: ioa })))
        .reduce((m, t) => Object.assign(m, t), {});
    },
  },
  actions: {
    async load() {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.ioa === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      if (this.moduleStatus === ModuleStatus.Loading) {
        return;
      }

      try {
        this.setStatus(ModuleStatus.Loading);

        const IoAs = await ioaService.getAll(brandStore.selectedClient, brandStore.selectedBrand, selectedCaseName);

        const sortedIoAs = IoAs.map((ioa) => {
          const dates = ioa.issueDates
            .map((d) => utils.toMoment(d, true) ?? moment())
            .sort((l: Moment, r: Moment) => l.valueOf() - r.valueOf());

          return {
            ...ioa,
            tags: ioa.tags.map((t) => ({
              ...t,
              classSafeName: t.name.replace(/\s/g, "").toLowerCase(),
              classSafeTagType: t.tagType.replace(/\s/g, "").toLowerCase(),
            })),
            startDate: dates[0]?.toDate(),
            endDate: dates.slice(-1)[0]?.toDate(),
          };
        })
          .sort((l, r) => l.startDate.getTime() - r.startDate.getTime())
          .map((ioa, index) => ({ ...ioa, index }));

        this.setInstanceOfAdvices(sortedIoAs);
      } catch (e) {
        if (isApiError(e) && e.status === 204) {
          this.setInstanceOfAdvices([]);
          return;
        }

        throw e;
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async add(payload: { instanceReference?: string }) {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.ioa === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      const selectedCaseFile = useCaseFileStore().selectedCaseFile;
      if (
        brandStore.selectedBrand == null ||
        brandStore.selectedClient == null ||
        selectedCaseName == null ||
        selectedCaseFile == null
      )
        return;

      if (this.moduleStatus === ModuleStatus.Loading) return;

      try {
        this.setStatus(ModuleStatus.Loading);
        await ioaService.add(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          selectedCaseFile,
          payload.instanceReference,
        );
      } finally {
        this.setStatus(ModuleStatus.Ready);
        await this.load();
        this.updateIoA();
      }
    },
    async remove(payload: { instanceReference: string }) {
      const brandStore = useBrandStore();
      if (brandStore.featuresConfig?.ioa === "disabled") return;

      const selectedCaseName = useCaseStore().selectedCaseName;
      const selectedCaseFile = useCaseFileStore().selectedCaseFile;
      if (
        brandStore.selectedBrand == null ||
        brandStore.selectedClient == null ||
        selectedCaseName == null ||
        selectedCaseFile == null
      )
        return;

      if (this.moduleStatus === ModuleStatus.Loading) return;

      try {
        this.setStatus(ModuleStatus.Loading);
        await ioaService.deleteIoa(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          selectedCaseFile,
          payload.instanceReference,
        );

        await useCaseStore().removeIoaFromAgreementPeriods(payload.instanceReference);
      } finally {
        this.setStatus(ModuleStatus.Ready);
        await this.load();
        this.updateIoA();
      }
    },

    // Mutations
    setInstanceOfAdvices(ioas: InstanceOfAdvice[]) {
      this.instanceOfAdvices = Object.freeze(ioas);
    },
    setStatus(status: ModuleStatus) {
      this.moduleStatus = status;
    },
    updateIoA() {
      // Do nothing. To update polling tier in polling plugin
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.instanceOfAdvices = [];
    },
  },
});
